// HUDA PORT — page: product detail (PDP).
// Ported from the static site's js/product.js (source of truth).
//
// WHAT SURVIVED, verbatim: the thumbnail switcher, the tier selector, the
// quantity stepper and the tab widget — including the RTL arrow-key direction
// (the NEXT tab sits to the visual left, so ArrowLeft advances) and the roving
// tabindex.
//
// WHAT IS GONE, and why: everything the source did to FETCH AND RENDER the page.
// js/product.js read ?handle= against assets/products.json and built the whole
// PDP in the DOM, because a static site has no server. Here Liquid renders it
// from the real product, so this file only wires behaviour to markup that
// already exists. That deletes the fetch, the not-found state, priceLabel(),
// appendRuns()/renderDescription(), renderGallery(), buildCard()/renderRelated()
// and the document.title rewrite — all of them now Shopify's job.
//
// WHAT CHANGED: the tier pills used to carry data-variant-gid for a future
// port; they now write the real numeric variant id into the form's `id` input,
// which is what Shopify's cart actually takes. And the add-to-cart button is a
// real form submit handled by hd-product-card.js, so the two pages share one
// cart implementation — the same consolidation the source's own header note
// describes between js/product.js and js/product-card.js.
(function () {
  'use strict';

  function slot(name, scope) {
    return (scope || document).querySelector('[data-pdp="' + name + '"]');
  }

  var els = {
    main: slot('main'),
    thumbs: slot('thumbs'),
    price: slot('price'),
    tiers: slot('tiers'),
    variantId: slot('variant-id'),
    qty: slot('qty'),
    qtyDec: slot('qty-dec'),
    qtyInc: slot('qty-inc'),
    form: slot('form')
  };

  // ------------------------------------------------------------- gallery
  // The thumbs are server-rendered; each carries the full-size source it
  // swaps in. aria-pressed is the single source of truth — hd-pdp.css styles
  // off the attribute and this file only flips it.
  function wireThumbs() {
    if (!els.thumbs || !els.main) return;

    var buttons = Array.prototype.slice.call(els.thumbs.querySelectorAll('.hd-pdp__thumb'));
    if (buttons.length < 2) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        els.main.src = btn.getAttribute('data-src');
        els.main.alt = btn.getAttribute('data-alt') || '';
        var w = btn.getAttribute('data-w');
        var h = btn.getAttribute('data-h');
        if (w) els.main.width = w;
        if (h) els.main.height = h;

        buttons.forEach(function (other) {
          other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
        });
      });
    });
  }

  // ---------------------------------------------------------------- tiers
  // Only the two tiered services render this block. The first pill is selected
  // server-side; a click owns exactly two things, the same two the source's
  // renderTiers owned: the displayed price and the cart target.
  function wireTiers() {
    if (!els.tiers) return;

    var pills = Array.prototype.slice.call(els.tiers.querySelectorAll('.hd-pdp__tier'));
    if (!pills.length) return;

    pills.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (els.price) els.price.textContent = btn.getAttribute('data-price');
        if (els.variantId) els.variantId.value = btn.getAttribute('data-variant-id');

        pills.forEach(function (other) {
          other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
        });
      });
    });
  }

  // ------------------------------------------------------------ quantity
  function wireQty() {
    if (!els.qty || !els.qtyDec || !els.qtyInc) return;

    function read() {
      var v = parseInt(els.qty.value, 10);
      return isNaN(v) || v < 1 ? 1 : v;
    }

    function set(v) {
      els.qty.value = String(v);
      els.qtyDec.disabled = v <= 1;
    }

    els.qtyDec.addEventListener('click', function () {
      set(Math.max(1, read() - 1));
    });
    els.qtyInc.addEventListener('click', function () {
      set(read() + 1);
    });
    // typing a blank or a 0 must not survive the blur
    els.qty.addEventListener('change', function () {
      set(read());
    });

    set(read());
  }

  // ----------------------------------------------------------------- tabs
  function wireTabs() {
    var list = document.querySelector('.hd-pdp__tablist');
    if (!list) return;
    var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    function select(tab) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1; // roving tabindex
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }

    // WAI-ARIA APG: the FORWARD arrow is the one pointing along the reading
    // direction, so ArrowLeft advances in rtl and ArrowRight advances in ltr.
    // This used to be hardcoded to the rtl sense. With today's TWO tabs it is a
    // provable no-op — (i+1)%2 and (i-1+2)%2 select the same tab — so the
    // English PDP is accidentally correct; it becomes a real defect the moment a
    // third tab is added. Derived rather than left as a landmine. Same one-line
    // idiom as the marquee sign and the slider's `rtl`.
    var fwd = getComputedStyle(list).direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(tab);
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === fwd) next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') next = tabs[(i - 1 + tabs.length) % tabs.length];
        // Home/End are logical first/last — direction-agnostic, no change
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        else return;
        e.preventDefault();
        select(next);
        next.focus();
      });
    });

    select(tabs[0]);
  }

  // ----------------------------------------------------------------- cart
  // One implementation, shared with the grid cards. Guarded: if that file
  // failed to load, the form is still a real POST and still adds to cart —
  // it just navigates to /cart instead of swapping the label.
  //
  // PHASE 3: that one implementation now also opens the cart drawer on a
  // successful add, so the PDP gained the drawer without a line of change here.
  // The header note above is amended accordingly: the add is no longer silent.
  function wireCart() {
    if (!els.form) return;
    if (window.HDProductCard) window.HDProductCard.wireCartForm(els.form);
  }

  wireThumbs();
  wireTiers();
  wireQty();
  wireTabs();
  wireCart();
})();
